/** Represents a single IANA timezone with its ISO country code and localization label key. */
export interface ITimezoneData {
  iana: string;
  countryCode: string;
  labelKey: string;
}

/** @returns The full list of supported timezone data entries. */
export function GetTimezoneList(): ITimezoneData[] {
  return timezoneData;
}

/**
 * Retrieves timezone data for a given IANA identifier.
 *
 * @param iana - The IANA timezone identifier (e.g. 'America/Los_Angeles').
 * @returns The timezone data object if found, undefined otherwise.
 */
export function GetTimezoneData(iana: string): ITimezoneData | undefined {
  return timezoneData.find((tz) => tz.iana === iana);
}

/**
 * Retrieves the ISO country code for a given IANA timezone.
 *
 * @param iana - The IANA timezone identifier.
 * @returns The ISO 3166-1 alpha-2 country code, or undefined if not found.
 */
export function GetCountryForTimezone(iana: string): string | undefined {
  return timezoneData.find((tz) => tz.iana === iana)?.countryCode;
}

/**
 * Retrieves all timezones associated with a given country code.
 *
 * @param countryCode - The ISO 3166-1 alpha-2 country code.
 * @returns An array of timezone data entries for that country.
 */
export function GetTimezonesForCountry(countryCode: string): ITimezoneData[] {
  return timezoneData.filter((tz) => tz.countryCode === countryCode);
}

/**
 * Generates a deterministic label key from an IANA timezone identifier.
 *
 * @param iana - The IANA timezone identifier.
 * @returns The generated label key.
 */
function toLabelKey(iana: string): string {
  return `Timezone${iana.replace(/[^a-zA-Z0-9]/g, '')}`;
}

/** Curated timezone list for North, Central, and South American markets plus key global hubs. */
const timezoneData: ITimezoneData[] = [
  // United States
  { iana: 'America/Los_Angeles', countryCode: 'US', labelKey: toLabelKey('America/Los_Angeles') },
  { iana: 'America/Denver', countryCode: 'US', labelKey: toLabelKey('America/Denver') },
  { iana: 'America/Phoenix', countryCode: 'US', labelKey: toLabelKey('America/Phoenix') },
  { iana: 'America/Boise', countryCode: 'US', labelKey: toLabelKey('America/Boise') },
  { iana: 'America/Chicago', countryCode: 'US', labelKey: toLabelKey('America/Chicago') },
  { iana: 'America/New_York', countryCode: 'US', labelKey: toLabelKey('America/New_York') },
  { iana: 'America/Detroit', countryCode: 'US', labelKey: toLabelKey('America/Detroit') },
  { iana: 'America/Indiana/Indianapolis', countryCode: 'US', labelKey: toLabelKey('America/Indiana/Indianapolis') },
  { iana: 'America/Kentucky/Louisville', countryCode: 'US', labelKey: toLabelKey('America/Kentucky/Louisville') },
  { iana: 'America/North_Dakota/Center', countryCode: 'US', labelKey: toLabelKey('America/North_Dakota/Center') },
  { iana: 'America/Anchorage', countryCode: 'US', labelKey: toLabelKey('America/Anchorage') },
  { iana: 'America/Adak', countryCode: 'US', labelKey: toLabelKey('America/Adak') },
  { iana: 'America/Honolulu', countryCode: 'US', labelKey: toLabelKey('America/Honolulu') },
  { iana: 'America/Puerto_Rico', countryCode: 'US', labelKey: toLabelKey('America/Puerto_Rico') },

  // Canada
  { iana: 'America/Vancouver', countryCode: 'CA', labelKey: toLabelKey('America/Vancouver') },
  { iana: 'America/Edmonton', countryCode: 'CA', labelKey: toLabelKey('America/Edmonton') },
  { iana: 'America/Regina', countryCode: 'CA', labelKey: toLabelKey('America/Regina') },
  { iana: 'America/Winnipeg', countryCode: 'CA', labelKey: toLabelKey('America/Winnipeg') },
  { iana: 'America/Toronto', countryCode: 'CA', labelKey: toLabelKey('America/Toronto') },
  { iana: 'America/Halifax', countryCode: 'CA', labelKey: toLabelKey('America/Halifax') },
  { iana: 'America/St_Johns', countryCode: 'CA', labelKey: toLabelKey('America/St_Johns') },
  { iana: 'America/Whitehorse', countryCode: 'CA', labelKey: toLabelKey('America/Whitehorse') },
  { iana: 'America/Yellowknife', countryCode: 'CA', labelKey: toLabelKey('America/Yellowknife') },
  { iana: 'America/Iqaluit', countryCode: 'CA', labelKey: toLabelKey('America/Iqaluit') },

  // Mexico
  { iana: 'America/Tijuana', countryCode: 'MX', labelKey: toLabelKey('America/Tijuana') },
  { iana: 'America/Hermosillo', countryCode: 'MX', labelKey: toLabelKey('America/Hermosillo') },
  { iana: 'America/Chihuahua', countryCode: 'MX', labelKey: toLabelKey('America/Chihuahua') },
  { iana: 'America/Mazatlan', countryCode: 'MX', labelKey: toLabelKey('America/Mazatlan') },
  { iana: 'America/Mexico_City', countryCode: 'MX', labelKey: toLabelKey('America/Mexico_City') },
  { iana: 'America/Cancun', countryCode: 'MX', labelKey: toLabelKey('America/Cancun') },
  { iana: 'America/Matamoros', countryCode: 'MX', labelKey: toLabelKey('America/Matamoros') },

  // Central America
  { iana: 'America/Panama', countryCode: 'PA', labelKey: toLabelKey('America/Panama') },
  { iana: 'America/Guatemala', countryCode: 'GT', labelKey: toLabelKey('America/Guatemala') },
  { iana: 'America/Tegucigalpa', countryCode: 'HN', labelKey: toLabelKey('America/Tegucigalpa') },
  { iana: 'America/San_Salvador', countryCode: 'SV', labelKey: toLabelKey('America/San_Salvador') },
  { iana: 'America/Managua', countryCode: 'NI', labelKey: toLabelKey('America/Managua') },
  { iana: 'America/Costa_Rica', countryCode: 'CR', labelKey: toLabelKey('America/Costa_Rica') },
  { iana: 'America/Belize', countryCode: 'BZ', labelKey: toLabelKey('America/Belize') },

  // Caribbean
  { iana: 'America/Havana', countryCode: 'CU', labelKey: toLabelKey('America/Havana') },
  { iana: 'America/Nassau', countryCode: 'BS', labelKey: toLabelKey('America/Nassau') },
  { iana: 'America/Jamaica', countryCode: 'JM', labelKey: toLabelKey('America/Jamaica') },
  { iana: 'America/Santo_Domingo', countryCode: 'DO', labelKey: toLabelKey('America/Santo_Domingo') },
  { iana: 'America/Barbados', countryCode: 'BB', labelKey: toLabelKey('America/Barbados') },

  // South America
  { iana: 'America/Sao_Paulo', countryCode: 'BR', labelKey: toLabelKey('America/Sao_Paulo') },
  { iana: 'America/Argentina/Buenos_Aires', countryCode: 'AR', labelKey: toLabelKey('America/Argentina/Buenos_Aires') },
  { iana: 'America/Santiago', countryCode: 'CL', labelKey: toLabelKey('America/Santiago') },
  { iana: 'America/Bogota', countryCode: 'CO', labelKey: toLabelKey('America/Bogota') },
  { iana: 'America/Lima', countryCode: 'PE', labelKey: toLabelKey('America/Lima') },
  { iana: 'America/Caracas', countryCode: 'VE', labelKey: toLabelKey('America/Caracas') },
  { iana: 'America/La_Paz', countryCode: 'BO', labelKey: toLabelKey('America/La_Paz') },
  { iana: 'America/Asuncion', countryCode: 'PY', labelKey: toLabelKey('America/Asuncion') },
  { iana: 'America/Montevideo', countryCode: 'UY', labelKey: toLabelKey('America/Montevideo') },
  { iana: 'America/Guayaquil', countryCode: 'EC', labelKey: toLabelKey('America/Guayaquil') },
  { iana: 'America/Cayenne', countryCode: 'GF', labelKey: toLabelKey('America/Cayenne') },
  { iana: 'America/Paramaribo', countryCode: 'SR', labelKey: toLabelKey('America/Paramaribo') },
  { iana: 'America/Guyana', countryCode: 'GY', labelKey: toLabelKey('America/Guyana') },

  // Europe
  { iana: 'Europe/London', countryCode: 'GB', labelKey: toLabelKey('Europe/London') },
  { iana: 'Europe/Paris', countryCode: 'FR', labelKey: toLabelKey('Europe/Paris') },
  { iana: 'Europe/Berlin', countryCode: 'DE', labelKey: toLabelKey('Europe/Berlin') },
  { iana: 'Europe/Istanbul', countryCode: 'TR', labelKey: toLabelKey('Europe/Istanbul') },

  // Asia-Pacific
  { iana: 'Asia/Tokyo', countryCode: 'JP', labelKey: toLabelKey('Asia/Tokyo') },
  { iana: 'Asia/Shanghai', countryCode: 'CN', labelKey: toLabelKey('Asia/Shanghai') },
  { iana: 'Asia/Singapore', countryCode: 'SG', labelKey: toLabelKey('Asia/Singapore') },
  { iana: 'Asia/Dubai', countryCode: 'AE', labelKey: toLabelKey('Asia/Dubai') },
  { iana: 'Australia/Sydney', countryCode: 'AU', labelKey: toLabelKey('Australia/Sydney') },
  { iana: 'Pacific/Auckland', countryCode: 'NZ', labelKey: toLabelKey('Pacific/Auckland') },

  // Global fallback
  { iana: 'Etc/UTC', countryCode: '', labelKey: toLabelKey('Etc/UTC') }
];

/**
 * English baseline display labels for every supported timezone, keyed by the same `labelKey` that {@link GetTimezoneList} entries carry. Each frontend app spreads this map into its `Localized`
 * object so the onboarding wizard, the business profile, and the admin portal all render the identical human-friendly timezone label (rather than a raw IANA identifier) for a given zone. Keeping
 * the single source here guarantees the lobby and portal never drift apart.
 */
export const TimezoneLabels: Record<string, string> = {
  TimezoneAmericaLosAngeles: 'Pacific (Los Angeles)',
  TimezoneAmericaDenver: 'Mountain (Denver)',
  TimezoneAmericaPhoenix: 'Arizona — No DST (Phoenix)',
  TimezoneAmericaBoise: 'Mountain (Boise)',
  TimezoneAmericaChicago: 'Central (Chicago)',
  TimezoneAmericaNewYork: 'Eastern (New York)',
  TimezoneAmericaDetroit: 'Eastern (Detroit)',
  TimezoneAmericaIndianaIndianapolis: 'Eastern (Indianapolis)',
  TimezoneAmericaKentuckyLouisville: 'Eastern (Louisville)',
  TimezoneAmericaNorthDakotaCenter: 'Central (Center, ND)',
  TimezoneAmericaAnchorage: 'Alaska (Anchorage)',
  TimezoneAmericaAdak: 'Hawaii-Aleutian (Adak)',
  TimezoneAmericaHonolulu: 'Hawaii (Honolulu)',
  TimezoneAmericaPuertoRico: 'Atlantic (Puerto Rico)',
  TimezoneAmericaVancouver: 'Pacific (Vancouver)',
  TimezoneAmericaEdmonton: 'Mountain (Edmonton)',
  TimezoneAmericaRegina: 'Saskatchewan — No DST (Regina)',
  TimezoneAmericaWinnipeg: 'Central (Winnipeg)',
  TimezoneAmericaToronto: 'Eastern (Toronto)',
  TimezoneAmericaHalifax: 'Atlantic (Halifax)',
  TimezoneAmericaStJohns: "Newfoundland (St. John's)",
  TimezoneAmericaWhitehorse: 'Yukon (Whitehorse)',
  TimezoneAmericaYellowknife: 'Mountain (Yellowknife)',
  TimezoneAmericaIqaluit: 'Eastern (Iqaluit)',
  TimezoneAmericaTijuana: 'Pacific (Tijuana)',
  TimezoneAmericaHermosillo: 'Mountain — No DST (Hermosillo)',
  TimezoneAmericaChihuahua: 'Mountain (Chihuahua)',
  TimezoneAmericaMazatlan: 'Mountain (Mazatlán)',
  TimezoneAmericaMexicoCity: 'Central (Mexico City)',
  TimezoneAmericaCancun: 'Eastern (Cancún)',
  TimezoneAmericaMatamoros: 'Central (Matamoros)',
  TimezoneAmericaPanama: 'Panama (Panama City)',
  TimezoneAmericaGuatemala: 'Central America (Guatemala City)',
  TimezoneAmericaTegucigalpa: 'Central America (Tegucigalpa)',
  TimezoneAmericaSanSalvador: 'Central America (San Salvador)',
  TimezoneAmericaManagua: 'Central America (Managua)',
  TimezoneAmericaCostaRica: 'Central America (San José)',
  TimezoneAmericaBelize: 'Central America (Belize City)',
  TimezoneAmericaHavana: 'Cuba (Havana)',
  TimezoneAmericaNassau: 'Bahamas (Nassau)',
  TimezoneAmericaJamaica: 'Jamaica (Kingston)',
  TimezoneAmericaSantoDomingo: 'Dominican Republic (Santo Domingo)',
  TimezoneAmericaBarbados: 'Barbados (Bridgetown)',
  TimezoneAmericaSaoPaulo: 'Brasília (São Paulo)',
  TimezoneAmericaArgentinaBuenosAires: 'Argentina (Buenos Aires)',
  TimezoneAmericaSantiago: 'Chile (Santiago)',
  TimezoneAmericaBogota: 'Colombia (Bogotá)',
  TimezoneAmericaLima: 'Peru (Lima)',
  TimezoneAmericaCaracas: 'Venezuela (Caracas)',
  TimezoneAmericaLaPaz: 'Bolivia (La Paz)',
  TimezoneAmericaAsuncion: 'Paraguay (Asunción)',
  TimezoneAmericaMontevideo: 'Uruguay (Montevideo)',
  TimezoneAmericaGuayaquil: 'Ecuador (Guayaquil)',
  TimezoneAmericaCayenne: 'French Guiana (Cayenne)',
  TimezoneAmericaParamaribo: 'Suriname (Paramaribo)',
  TimezoneAmericaGuyana: 'Guyana (Georgetown)',
  TimezoneEuropeLondon: 'GMT (London)',
  TimezoneEuropeParis: 'Central European (Paris)',
  TimezoneEuropeBerlin: 'Central European (Berlin)',
  TimezoneEuropeIstanbul: 'Turkey (Istanbul)',
  TimezoneAsiaTokyo: 'Japan (Tokyo)',
  TimezoneAsiaShanghai: 'China (Shanghai)',
  TimezoneAsiaSingapore: 'Singapore',
  TimezoneAsiaDubai: 'Gulf (Dubai)',
  TimezoneAustraliaSydney: 'Australia Eastern (Sydney)',
  TimezonePacificAuckland: 'New Zealand (Auckland)',
  TimezoneEtcUTC: 'Coordinated Universal Time (UTC)'
};
